import { create } from 'zustand';
import { Session } from '@/types/sessions.types';

export interface SessionItem {
  id: string;
  name: string;
  tabId?: string[];
  tabOrgId?: (number | string)[];
  lastActiveTabId?: string | null;
  iconEmoji?: string;
}

/** Convert a Session to a SessionItem (emoji-resolved icon, id/name/tabs only) */
function toSessionItem(s: Session): SessionItem {
  let iconEmoji: string | undefined;
  if (s.icon) {
    if (/^[0-9A-Fa-f]+$/.test(s.icon)) {
      try { iconEmoji = String.fromCodePoint(parseInt(s.icon, 16)); } catch { iconEmoji = s.icon; }
    } else {
      iconEmoji = s.icon;
    }
  }
  return { id: s.sid, name: s.name, tabId: s.tabId, tabOrgId: s.tabOrgId, iconEmoji };
}

interface SessionsState {
  /** Full Session objects — single source of truth for sidebar + any consumer */
  allSessions: Session[];
  unsavedSessions: SessionItem[];
  projectSessions: SessionItem[];
  // Raw sessions data from extension (all sessions with full details)
  rawAllSessions: Record<string, Session>;
  sessionUpdate: Record<string, unknown>;
  // Map of tabId to tabOrgId for the current session
  // Single source of truth - updated via extension events
  currentSessionTabOrgIdMap: Map<string, string | number>;
  setSessionUpdate: (data: Record<string, unknown>) => void;
  /**
   * Primary setter for sessions. Accepts a new array or an updater function.
   * Automatically keeps unsavedSessions, projectSessions and rawAllSessions in sync.
   */
  setAllSessions: (sessions: Session[] | ((prev: Session[]) => Session[])) => void;
  setUnsavedSessions: (sessions: SessionItem[]) => void;
  setProjectSessions: (sessions: SessionItem[]) => void;
  setSessions: (unsaved: SessionItem[], projects: SessionItem[]) => void;
  setRawAllSessions: (sessions: Record<string, Session>) => void;
  /** True once the initial sessions fetch (on app load) has completed */
  sessionsLoaded: boolean;
  setSessionsLoaded: (loaded: boolean) => void;
  /** In-flight load promise so StrictMode / duplicate callers share one /sessions request */
  sessionsLoadInFlight: Promise<void> | null;
  setSessionsLoadInFlight: (promise: Promise<void> | null) => void;
  /** The session that was active when the extension last reported (csid from getAllSessions). */
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  /** Sessions with open Chrome windows (multi-monitor); from extension activeSessions. */
  activeSessionIds: string[];
  setActiveSessionIds: (ids: string[]) => void;
  setCurrentSessionTabOrgIdMap: (tabIdArray: string[], tabOrgIdArray: (string | number)[]) => void;
  // Update a single tabOrgId pair (called when extension sends replace events)
  updateTabOrgId: (tabId: string, tabOrgId: string | number) => void;
  getTabOrgId: (tabId: string) => string | number | undefined;
  // Get tabId from tabOrgId (reverse lookup)
  getTabIdFromOrgId: (tabOrgId: string | number) => string | undefined;
  /** Clears session state when workspace account (aid) changes. */
  resetForAccountSwitch: () => void;
}

/**
 * Sessions store
 * Manages available sessions (unsaved and project sessions),
 * raw session data from extension, and current session's tabId-to-tabOrgId mapping
 * 
 * Single source of truth for:
 * - rawAllSessions: Complete session data from extension
 * - tabOrgId mapping: Updated via extension events and accessed via getTabOrgId()
 * Accessible globally from any component
 */
export const useSessionsStore = create<SessionsState>((set, get) => ({
  allSessions: [],
  unsavedSessions: [],
  projectSessions: [],
  rawAllSessions: {},
  sessionUpdate:{},
  currentSessionTabOrgIdMap: new Map(),
  sessionsLoaded: false,
  setSessionsLoaded: (loaded) => set({ sessionsLoaded: loaded }),
  sessionsLoadInFlight: null,
  setSessionsLoadInFlight: (promise) => set({ sessionsLoadInFlight: promise }),
  activeSessionId: null,
  setActiveSessionId: (id) => set({ activeSessionId: id }),
  activeSessionIds: [],
  setActiveSessionIds: (ids) =>
    set({ activeSessionIds: Array.isArray(ids) ? ids.filter(Boolean) : [] }),
  setAllSessions: (sessions) => {
    const next = typeof sessions === 'function' ? sessions(get().allSessions) : sessions;
    const unsaved = next.filter(s => s.isWorkSpace === false).map(toSessionItem);
    const projects = next.filter(s => s.isWorkSpace === true).map(toSessionItem);
    const raw: Record<string, Session> = {};
    next.forEach(s => { raw[s.sid] = s; });
    set({ allSessions: next, unsavedSessions: unsaved, projectSessions: projects, rawAllSessions: raw });
  },
  setUnsavedSessions: (sessions) => set({ unsavedSessions: sessions }),
  setProjectSessions: (sessions) => set({ projectSessions: sessions }),
  setSessionUpdate: (data) => {
    // Deduplicate: SW broadcasts the same event to every open tab, so the same
    // payload arrives multiple times. Only update state when the event fingerprint
    // actually changes — this prevents unnecessary re-renders in subscribers.
    // For events that carry mutable tab content (updated, created) we also include
    // tab.url + tab.title + inx so that distinct payloads for the same tab are
    // never dropped (e.g. two consecutive "updated" events with different titles).
    // For session-level events (newsession, deletesession) we include session.sid
    // so that two different sessions with the same csid are never deduplicated.
    //
    // User-triggered previews (e.g. categorizePreview) must re-fire even when
    // the payload is unchanged — otherwise reopening a modal after close is ignored.
    const type = data?.type as string | undefined;
    const alwaysDeliver = type === 'categorizePreview';
    const payload = alwaysDeliver ? { ...data, _eventAt: Date.now() } : data;

    if (!alwaysDeliver) {
      const prev = get().sessionUpdate;
      const prevFingerprint = JSON.stringify(prev);
      const nextFingerprint = JSON.stringify(payload);
      if (prevFingerprint === nextFingerprint) {
        console.warn('🚫 DEDUPLICATED - same session update, skipping', payload);
        return;
      }
    }
    set({ sessionUpdate: payload });
  },
  setSessions: (unsaved, projects) =>
    set({ unsavedSessions: unsaved, projectSessions: projects }),
  setRawAllSessions: (sessions) => set({ rawAllSessions: sessions }),
  setCurrentSessionTabOrgIdMap: (tabIdArray, tabOrgIdArray) => {
    const map = new Map<string, string | number>();
    tabIdArray.forEach((tabId, idx) => {
      const orgId = tabOrgIdArray[idx];
      if (orgId !== undefined) {
        map.set(tabId, orgId);
      }
    });
    set({ currentSessionTabOrgIdMap: map });
  },
  updateTabOrgId: (tabId: string, tabOrgId: string | number) => {
    const map = new Map(get().currentSessionTabOrgIdMap);
    map.set(tabId, tabOrgId);
    set({ currentSessionTabOrgIdMap: map });
  },
  getTabOrgId: (tabId: string) => {
    return get().currentSessionTabOrgIdMap.get(tabId);
  },
  getTabIdFromOrgId: (tabOrgId: string | number) => {
    const map = get().currentSessionTabOrgIdMap;
    // Convert tabOrgId to string for comparison since Map keys might be stored differently
    const orgIdStr = String(tabOrgId);
    for (const [tabId, orgId] of map.entries()) {
      if (String(orgId) === orgIdStr) {
        return tabId;
      }
    }
    return undefined;
  },
  resetForAccountSwitch: () =>
    set({
      allSessions: [],
      unsavedSessions: [],
      projectSessions: [],
      rawAllSessions: {},
      sessionUpdate: {},
      currentSessionTabOrgIdMap: new Map(),
      sessionsLoaded: false,
      sessionsLoadInFlight: null,
      activeSessionId: null,
      activeSessionIds: [],
    }),
}));
